import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, User } from 'lucide-react';
import { getApiUrl } from '../lib/api';

const API = getApiUrl();
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function ProfileAnalysis() {
  const [image, setImage] = useState(() => localStorage.getItem('profile_image') || null);
  const [preview, setPreview] = useState(() => localStorage.getItem('profile_preview') || null);
  const [visualIdentity, setVisualIdentity] = useState(() => localStorage.getItem('profile_visualIdentity') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(() => {
    const saved = localStorage.getItem('profile_result');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (image) localStorage.setItem('profile_image', image);
    else localStorage.removeItem('profile_image');
  }, [image]);

  useEffect(() => {
    if (preview) localStorage.setItem('profile_preview', preview);
    else localStorage.removeItem('profile_preview');
  }, [preview]);

  useEffect(() => {
    localStorage.setItem('profile_visualIdentity', visualIdentity);
  }, [visualIdentity]);

  useEffect(() => {
    if (result) localStorage.setItem('profile_result', JSON.stringify(result));
    else localStorage.removeItem('profile_result');
  }, [result]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setImage(reader.result.split(',')[1]); setPreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!image) { alert('Envie um print do seu perfil primeiro.'); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai/analyze-profile`, { image, visualIdentity }, auth());
      setResult(data);
    } catch { alert('Erro ao analisar. Tente novamente.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '28px', background: '#080808' }}>
      <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#E0E0E0', marginBottom: '6px' }}>Análise de Perfil</h1>
      <p style={{ color: '#999', fontSize: '13px', marginBottom: '28px' }}>Envie um print do seu perfil do Instagram e receba uma análise estratégica completa.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '900px' }}>
        {/* Upload */}
        <div style={{ background: '#111', border: '1px solid #1E0505', borderRadius: '12px', padding: '24px' }}>
          <h3 style={{ color: '#E0E0E0', fontSize: '14px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={15} style={{ color: '#C0392B' }} /> Print do Perfil
          </h3>
          <label style={{ display: 'block', border: '2px dashed #2A0808', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#C0392B'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#2A0808'}>
            {preview ? (
              <img src={preview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'cover' }} />
            ) : (
              <>
                <Upload size={28} style={{ color: '#C0392B', margin: '0 auto 8px' }} />
                <p style={{ color: '#AAA', fontSize: '13px' }}>Clique para enviar o print</p>
              </>
            )}
            <input type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
          </label>

          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: '#AAA', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Identidade Visual e Posicionamento
            </label>
            <textarea value={visualIdentity} onChange={e => setVisualIdentity(e.target.value)}
              placeholder="Ex: Minha paleta é dourado e preto, quero um posicionamento de autoridade..."
              rows={3} className="fire-input" style={{ resize: 'none', fontFamily: 'inherit', fontSize: '13px' }} />
          </div>

          <button onClick={analyze} disabled={loading || !image} className="fire-btn"
            style={{ width: '100%', marginTop: '16px', padding: '12px' }}>
            {loading ? 'Analisando...' : 'Analisar Perfil'}
          </button>
        </div>

        {/* Resultado */}
        <div style={{ background: '#111', border: '1px solid #1E0505', borderRadius: '12px', padding: '24px' }}>
          <h3 style={{ color: '#E0E0E0', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>Análise Estratégica</h3>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{ height: '16px', background: '#1A0505', borderRadius: '4px', animation: 'pulse 1.5s infinite', width: i % 2 === 0 ? '100%' : '75%' }} />
              ))}
            </div>
          )}
          {result && !loading && (
            <div style={{ color: '#CCC', fontSize: '13px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
              {result.analysisText}
              {result.imageUrl && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ color: '#C0392B', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>PERFIL SUGERIDO:</p>
                  <img src={result.imageUrl} alt="Perfil melhorado" style={{ width: '100%', borderRadius: '8px', border: '1px solid #2A0808' }} />
                </div>
              )}
            </div>
          )}
          {!result && !loading && (
            <p style={{ color: '#777', fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
              Envie um print e clique em Analisar
            </p>
          )}
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:0.7} }`}</style>
    </div>
  );
}
